import { useEffect, useState } from 'react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFile, faTable } from '@fortawesome/free-solid-svg-icons';

type ChildProps = {
  labels: string[],
  remainingDebtPerYear: number[],
  totalAmountPaidPerYear: number[],
  amountPaidYearly: number[],
  interestAddedPerYear: number[],
  interestPaidPerYear: number[],
  remainingDebtPerMonth: number[],
  totalAmountPaidPerMonth: number[],
  amountPaidMonthly: number[],
  interestAddedPerMonth: number[],
  interestPaidPerMonth: number[],
};

type DataEntry = {
  timeLabel: string,
  interestAdded: number,
  interestPaid: number,
  amountPaid: number,
  amountPaidPeriod: number,
  remainingDebt: number
};

function TableModeller({ labels, remainingDebtPerYear, totalAmountPaidPerYear, amountPaidYearly, interestAddedPerYear, interestPaidPerYear, remainingDebtPerMonth, totalAmountPaidPerMonth, amountPaidMonthly, interestAddedPerMonth, interestPaidPerMonth }: ChildProps) {
  const [data, setData] = useState<DataEntry[]>([]);
  const [scale, setScale] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    const formattedYearlyData: DataEntry[] = [...labels, String(labels.length)].reduce((acc, label, index) => {
      if (remainingDebtPerYear[index] < 0 || (acc.length > 0 && acc[acc.length - 1].remainingDebt <= 0)) {
        // Once an entry with 0 or less debt is added, do not add more.
        return acc;
      }
      const entry: DataEntry = {
        timeLabel: label,
        interestAdded: interestAddedPerYear[index],
        interestPaid: interestPaidPerYear[index],
        amountPaidPeriod: amountPaidYearly[index],
        amountPaid: totalAmountPaidPerYear[index],
        remainingDebt: remainingDebtPerYear[index],
      };
      acc.push(entry);
      return acc;
    }, [] as DataEntry[]);

    const formattedMonthlyData: DataEntry[] = labels.flatMap((label, yearIndex) => {
      const monthlyEntries: DataEntry[] = [];
      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const index = yearIndex * 12 + monthIndex;
        if (index >= remainingDebtPerMonth.length || (monthlyEntries.length > 0 && monthlyEntries[monthlyEntries.length - 1].remainingDebt <= 0)) {
          // Stop adding new entries once an entry with 0 or less debt has been added
          break;
        }
        monthlyEntries.push({
          timeLabel: `${yearIndex}Y ${monthIndex}M`,
          interestAdded: interestAddedPerMonth[index],
          interestPaid: interestPaidPerMonth[index],
          amountPaidPeriod: amountPaidMonthly[index],
          amountPaid: totalAmountPaidPerMonth[index],
          remainingDebt: remainingDebtPerMonth[index],
        });
      }
      return monthlyEntries;
    });
    const formattedData: DataEntry[] = scale === 'yearly' ? formattedYearlyData : formattedMonthlyData;
    setData(formattedData);
  }, [scale, labels, remainingDebtPerYear, totalAmountPaidPerYear, amountPaidYearly, interestAddedPerYear, interestPaidPerYear, remainingDebtPerMonth, totalAmountPaidPerMonth, amountPaidMonthly, interestAddedPerMonth, interestPaidPerMonth]);

  const handleScaleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setScale(event.target.value as 'monthly' | 'yearly');
  };

  const handleExportCSVData = () => {
    const headers = scale === 'yearly' ? [
      { key: 'timeLabel', title: 'Year' },
      { key: 'interestAdded', title: 'Interest Added Yearly' },
      { key: 'interestPaid', title: 'Total Interest Paid Yearly' },
      { key: 'amountPaidPeriod', title: 'Amount Paid Yearly' },
      { key: 'amountPaid', title: 'Total Amount Paid Yearly' },
      { key: 'remainingDebt', title: 'Remaining Debt' }
    ] : [
      { key: 'timeLabel', title: 'Year Month' },
      { key: 'interestAdded', title: 'Interest Added Monthly' },
      { key: 'interestPaid', title: 'Total Interest Paid Monthly' },
      { key: 'amountPaidPeriod', title: 'Amount Paid Monthly' },
      { key: 'amountPaid', title: 'Total Amount Paid Monthly' },
      { key: 'remainingDebt', title: 'Remaining Debt' }
    ];

    // Transform data to match the structure expected by export-to-csv
    const transformedData = data.map(entry => ({
      [headers[0].title]: entry.timeLabel,
      [headers[1].title]: entry.interestAdded,
      [headers[2].title]: entry.interestPaid,
      [headers[3].title]: entry.amountPaidPeriod,
      [headers[4].title]: entry.amountPaid,
      [headers[5].title]: entry.remainingDebt,
    }));
    const fileName = scale === 'yearly' ? 'mortgage-table-yearly-data' : 'mortgage-table-monthly-data';
    const csvConfig = mkConfig({
      fieldSeparator: ',',
      filename: fileName,
      decimalSeparator: '.',
      useKeysAsHeaders: true,
    });
    const csv = generateCsv(csvConfig)(transformedData);
    download(csvConfig)(csv);
  };

  const handleExportPDFData = () => {
    const doc = new jsPDF();
    const tableData = data.map((entry) => Object.values(entry));
    const tableHeaders = scale === 'yearly' ?
      ["Year", "Interest Added Yearly", "Total Interest Paid", "Amount Paid Yearly", "Total Amount Paid", "Remaining Debt"] :
      ["Year Month", "Interest Added Monthly", "Total Interest Paid Monthly", "Amount Paid Monthly", "Total Amount Paid Monthly", "Remaining Debt"];
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });
    doc.save('mortgage-table-data.pdf');
  };

  return (
    <div>
      <div className="pb-4">
        <div className="bg-base-300 rounded-box p-3">
          <div className="flex justify-between items-center">
            <h1 className="text-4xl font-bold"><FontAwesomeIcon className="mr-2" icon={faTable} />Table</h1>
            <div className="flex gap-4 items-end">
              <button className="text-l font-semibold p-3 btn-primary rounded-full flex items-center justify-center" onClick={handleExportCSVData}>Export to CSV <FontAwesomeIcon className="ml-2" icon={faFileExcel} /></button>
              <button className="text-l font-semibold p-3 btn-accent rounded-full flex items-center justify-center" onClick={handleExportPDFData}>Export to PDF <FontAwesomeIcon className="ml-2" icon={faFile} /></button>
              <label className="flex flex-col">
                <span className="text-sm font-medium text-gray-900 mb-2">Choose Time Scale:</span>
                <select id="timeScaleSelect" onChange={handleScaleChange} className="select select-bordered">
                  <option disabled value="">Select your time scale</option>
                  <option selected value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg" style={{ height: '80vh', borderRadius: 'var(--rounded-box, 1rem)' }}>
        <table className="table table-xs table-pin-rows table-pin-cols min-w-full divide-y divide-gray-500">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-base-300">{scale === 'yearly' ? 'Year' : 'Y M'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-base-300">Interest Added {scale === 'yearly' ? 'Yearly' : 'Monthly'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-base-300">Total Interest Paid</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-base-300">Amount Paid {scale === 'yearly' ? 'Yearly' : 'Monthly'}</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-base-300">Total Amount Paid</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-base-300">Remaining Debt</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold bg-base-300">0Y 0M</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300" style={{ color: '#CD3841' }}>£0</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300">£0</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300" style={{ color: '#24955D' }}>£0</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300">£0</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold bg-base-300">£0</td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold bg-base-300">{row.timeLabel}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300" style={{ color: '#CD3841' }}>£{row.interestAdded}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300">£{row.interestPaid}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300" style={{ color: '#24955D' }}>£{row.amountPaidPeriod}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium bg-base-300">£{row.amountPaid}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold bg-base-300">£{row.remainingDebt}</td>
                </tr>
              ))
            )}
          </tbody>

        </table>
      </div>
    </div>
  );
}

export default TableModeller;
